// Pure helpers for the medical file: the chart's board order, Arabic count
// agreement, file sizes, status/condition copy. No React, no I/O — unit-tested
// in medical-lib.test.ts. Nothing here sums or derives a figure the API owns
// (rule 7): plan totals, counts and sizes arrive from erp-core; these only
// FORMAT them.

import { arNumber } from '@/lib/format';
import type { PillTone } from '@/components';
import type { ProcedureStatus, ToothConditionKind, ToothState } from '../types';

/** The platform name token («[اسم المنصة]» on the boards) — never typed into a screen. */
export const PLATFORM_NAME = 'احجزلي';

/** Sectors that carry the medical module (task: clinic / dental AND erp.medical). */
export const MEDICAL_SECTORS: readonly string[] = ['clinic', 'dental'];

export function isMedicalSector(sector: string | null | undefined): boolean {
  return !!sector && MEDICAL_SECTORS.includes(sector.toLowerCase());
}

/**
 * Board order of the two arches (board 14 `customers/medical--dental-chart`):
 * upper 18…11 · 21…28, lower 48…41 · 31…38. In RTL the first cell sits at the
 * inline start (right), so quadrant 1 is above quadrant 4 and 2 above 3.
 */
export const UPPER_ARCH: readonly number[] = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const LOWER_ARCH: readonly number[] = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

/** Phone (<768): one row of eight per quadrant, same reading order. */
export const QUADRANT_ROWS: readonly (readonly number[])[] = [UPPER_ARCH.slice(0, 8), UPPER_ARCH.slice(8), LOWER_ARCH.slice(0, 8), LOWER_ARCH.slice(8)];

/** Every tooth in keyboard order (upper then lower arch). */
export const CHART_ORDER: readonly number[] = [...UPPER_ARCH, ...LOWER_ARCH];

/** «الزيارة» → «زيارة» (construct state for «تم إنهاء زيارة …», «لا مراجع …»). */
export function stripAl(word: string): string {
  return word.startsWith('ال') ? word.slice(2) : word;
}

interface Forms {
  one: string;
  two: string;
  /** 3–10, the number is prefixed. */
  few: string;
  /** 11+, the number is prefixed. */
  many: string;
}

/** Arabic count agreement: 1 → one, 2 → two, 3–10 → «N few», else «N many». */
export function countLabel(n: number, f: Forms): string {
  if (n === 1) return f.one;
  if (n === 2) return f.two;
  if (n >= 3 && n <= 10) return `${arNumber(n)} ${f.few}`;
  return `${arNumber(n)} ${f.many}`;
}

export const proceduresLabel = (n: number) => countLabel(n, { one: 'إجراء واحد', two: 'إجراءان', few: 'إجراءات', many: 'إجراءً' });
export const filesLabel = (n: number) => countLabel(n, { one: 'ملف واحد', two: 'ملفان', few: 'ملفات', many: 'ملفاً' });
export const drugsLabel = (n: number) => countLabel(n, { one: 'دواء واحد', two: 'دواءان', few: 'أدوية', many: 'دواءً' });

const MIB = 1024 * 1024;

/** Bytes → «٢٫١ ميغابايت» (one decimal; whole numbers bare: «١٠ ميغابايت»). */
export function formatMegabytes(bytes: number): string {
  const mb = bytes / MIB;
  const whole = Math.abs(mb - Math.round(mb)) < 0.05;
  return `${arNumber(whole ? Math.round(mb) : mb, { decimals: whole ? 0 : 1 })} ميغابايت`;
}

/** «أنثى» / «ذكر» from the BFF gender; undefined when the record has none. */
export function genderWord(gender?: string | null): string | undefined {
  if (gender === 'female') return 'أنثى';
  if (gender === 'male') return 'ذكر';
  return undefined;
}

/** «لها حساب في احجزلي» / «له حساب في احجزلي» (masculine when gender is unknown). */
export function accountPhrase(gender?: string | null): string {
  return `${gender === 'female' ? 'لها' : 'له'} حساب في ${PLATFORM_NAME}`;
}

/** Procedure status pill (spec board 6 §4: ok / warn / neutral). */
export const PROCEDURE_STATUS: Record<ProcedureStatus, { label: string; tone: PillTone }> = {
  done: { label: 'مُنجز', tone: 'ok' },
  in_progress: { label: 'اليوم · جارٍ', tone: 'warn' },
  planned: { label: 'مخطَّط', tone: 'neutral' },
  cancelled: { label: 'ملغى', tone: 'neutral' },
};

/** Legend order + copy of the chart card. */
export const LEGEND: readonly { state: 'done' | 'planned' | 'missing' | 'selected'; label: string }[] = [
  { state: 'done', label: 'مُنجز' },
  { state: 'planned', label: 'مخطَّط' },
  { state: 'missing', label: 'مفقود' },
  { state: 'selected', label: 'المختار' },
];

/** Tooth condition words (the latest entry of the per-tooth log). */
export const CONDITION_LABEL: Record<ToothConditionKind, string> = {
  healthy: 'سليم',
  caries: 'تسوّس',
  filled: 'حشوة',
  crown: 'تاج',
  root_canal: 'علاج جذور',
  missing: 'مفقود',
  implant: 'زرعة',
  fractured: 'كسر',
  other: 'أخرى',
};

/** Selected-tooth line status phrase. */
export function toothStatePhrase(state: ToothState | ProcedureStatus): string | undefined {
  switch (state) {
    case 'in_progress':
      return 'جارٍ اليوم';
    case 'planned':
      return 'مخطَّط';
    case 'done':
      return 'مُنجز';
    default:
      return undefined;
  }
}

/** Upload validation copy — the same Arabic sentences erp-core answers with. */
export const UPLOAD_COPY = {
  tooLarge: 'الملف أكبر من ١٠ ميغابايت',
  notAllowed: 'يُقبل ملف صورة (JPEG/PNG/WebP) أو PDF فقط',
} as const;

/** Client pre-check against the API's own limits (max_bytes, accepted_types). */
export function checkUpload(file: { size: number; type: string }, maxBytes: number, accepted: readonly string[]): string | null {
  if (!accepted.includes(file.type)) return UPLOAD_COPY.notAllowed;
  if (file.size > maxBytes) return `الملف أكبر من ${formatMegabytes(maxBytes)}`;
  return null;
}
